import { useCallback, useEffect, useRef } from 'react';

import { USER_ACTIVITY_WINDOW_MS } from '@/pages/workgroup/form/setting/constants';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

// Coalesce the high-frequency events; we only care about the timestamp, not every sample.
const THROTTLE_MS = 1000;

/**
 * Tracks whether the user is genuinely present: the tab is visible AND they produced input
 * recently.
 *
 * Both halves matter for auto-pause. Without the visibility check a background tab would keep a
 * workspace running; without the input check an abandoned foreground tab would do the same, and
 * the workspace would bill indefinitely with nobody watching.
 *
 * The last-activity timestamp is kept in a ref rather than state on purpose: this updates on every
 * mouse move, and routing that through setState would re-render the subtree continuously.
 */
export function useUserActivity(activityWindowMs: number = USER_ACTIVITY_WINDOW_MS) {
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    let lastRecorded = 0;

    const record = () => {
      const now = Date.now();
      if (now - lastRecorded < THROTTLE_MS) {
        return;
      }
      lastRecorded = now;
      lastActivityRef.current = now;
    };

    const onVisibilityChange = () => {
      // Returning to the tab is itself a signal of presence.
      if (document.visibilityState === 'visible') {
        record();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, record, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, record));
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const isActive = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return false;
    }
    return Date.now() - lastActivityRef.current <= activityWindowMs;
  }, [activityWindowMs]);

  const getLastActivity = useCallback(() => lastActivityRef.current, []);

  return { isActive, getLastActivity };
}
